import { FC, useCallback, useContext } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Divider, Grid, Link, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import config from '@shared/config';
import { LocalizationContext } from '@cvt/contexts';
import { emailRegex } from '@cvt/helpers/validation';
import { useRouter } from '@cvt/routing';

import { useSignin } from '../hooks/useSignin';
import { LoggedOutActionsLayout } from '../components/LoggedOutActionsLayout';

interface Props {
  onLoggedIn?: () => void;
  onNoAccount?: () => void;
  onGoToResetPassword?: () => void;
};

export const Login: FC<Props> = (props) => {
  const { onLoggedIn, onNoAccount, onGoToResetPassword } = props;
  const router = useRouter();

  const { dictionary } = useContext(LocalizationContext);
  const { control, setError, handleSubmit, formState: { isSubmitting, errors } } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { signIn, signInWithSSO } = useSignin();

  const onSSOLogin = useCallback(async (provider: string) => {
    try {
      await signInWithSSO(provider);
      onLoggedIn?.();
    } catch (error: any) {
      if (['auth/account-exists-with-different-credential'].includes(error.error)) {
        // @ts-ignore
        setError('email', { type: 'error', message: dictionary.auth.firebase.errors[`${error.error}:${provider}`] || error.message });
      } else {
        // @ts-ignore
        setError('email', { type: 'error', message: dictionary.auth.firebase.errors[error.error] || error.message });
      }
    }
  }, [signInWithSSO, onLoggedIn, setError, dictionary.auth.firebase.errors]);

  const onSubmit = useCallback (async ({ email, password }: { email: string; password: string }) => {
    try {
      await signIn(email, password);
      onLoggedIn?.();
    } catch (e: any) {
      if(['auth/invalid-email','auth/user-not-found', 'auth/wrong-password'].includes(e.error)) {
        setError('email', { type: 'error', message: dictionary.auth.validations.emailOrPasswordWrong });
        setError('password', { type: 'error', message: dictionary.auth.validations.emailOrPasswordWrong });
      } else {
        setError('email', { type: 'error', message: dictionary.errors.somethingWentWrong });
      }
    }
  }, [signIn, setError, dictionary, onLoggedIn]);

  return (
    <LoggedOutActionsLayout>
      <Typography variant="h4" align="center">{dictionary.auth.login.title}</Typography><br/>

      <Stack
        spacing={1.2}
        width="100%"
        mb={1.2}
      >
        {config.featureFlags.googleSSO && (
          <Button
            onClick={() => onSSOLogin('google')}
            variant="contained"
            color="primary"
            startIcon={
              <img src="/google.svg" alt="Google logo" width={20} height={20} />
            }
            aria-label={dictionary.auth.login.buttonLoginWithGoogle}
            size="medium"
            fullWidth
            sx={{
              minWidth: 0,
              py: 1.2,
              fontWeight: 600,
              fontSize: '1rem',
              boxShadow: 1,
            }}
          >
            {dictionary.auth.login.buttonLoginWithGoogle}
          </Button>
        )}
        {config.featureFlags.metaSSO && (
          <Button
            onClick={() => onSSOLogin('facebook')}
            variant="contained"
            sx={{
              bgcolor: '#1877F2',
              color: '#fff',
              '&:hover': { bgcolor: '#1151A3' },
              minWidth: 0,
              py: 1.2,
              fontWeight: 600,
              fontSize: '1rem',
              boxShadow: 1,
            }}
            startIcon={
              <img src="/facebook.svg" alt="Facebook logo" width={20} height={20} />
            }
            aria-label={dictionary.auth.login.buttonLoginWithMeta}
            size="medium"
            fullWidth
          >
            {dictionary.auth.login.buttonLoginWithMeta}
          </Button>
        )}
      </Stack>

      <Box my={2}>
        <Divider orientation="horizontal" flexItem>
          <Typography variant="body2" color="textSecondary">
            {dictionary.auth.login.orLoginWithEmail}
          </Typography>
        </Divider>
      </Box>

      <div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container direction="column">
            <Grid size={12}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: dictionary.forms.validations.required,
                  pattern: {
                    value: emailRegex,
                    message: dictionary.forms.validations.invalidEmail,
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label={dictionary.forms.fieldEmail}
                    name="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>
            <br/>
            <Grid size={12}>
              <Controller
                name="password"
                control={control}
                rules={{ required: dictionary.forms.validations.required }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label={dictionary.forms.fieldPassword}
                    name="password"
                    type="password"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                  />
                )}
              />
              <br/>
              <Typography align="right">
                <Link
                  {...(onGoToResetPassword ? {
                    onClick: onGoToResetPassword,
                  } : {
                    component: RouterLink,
                    to: router.auth.resetPassword.path,
                  })}
                  color="primary"
                  variant="body2"
                >
                  {dictionary.auth.login.buttonResetPassword}
                </Link>
              </Typography>
            </Grid>
            <Grid size={12} pt={2}>
              <LoadingButton
                fullWidth
                type="submit"
                loading={isSubmitting}
                size="large"
                variant="contained"
                color="primary"
              >
                {dictionary.auth.login.buttonLogin}
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      </div>

      <Box mt={4} textAlign="center">
        <Typography variant="body2">
          {dictionary.auth.login.dontHaveAccount}
        </Typography>
        <Box mt={2}>
          <Button
            {...(onNoAccount ? {
              onClick: onNoAccount,
            } : {
              component: RouterLink,
              to: router.auth.signup.path,
            })}
            variant="contained"
            color="secondary"
            size="large"
          >
            {dictionary.auth.login.buttonCreateAccount}
          </Button>
        </Box>
      </Box>
    </LoggedOutActionsLayout>
  );
};
